import React, { useState, useEffect } from 'react';
import {
    Drawer,
    List,
    Button,
    Select,
    Space,
    Typography,
    Divider,
    message,
    Popconfirm,
    Empty,
    Avatar,
    Tag
} from 'antd';
import {
    UserOutlined,
    UserAddOutlined,
    DeleteOutlined,
    TeamOutlined
} from '@ant-design/icons';
import { campaignAgentService } from '../services/api';

const { Text, Title } = Typography;
const { Option } = Select;

const CampaignAgentsDrawer = ({ visible, onClose, campaign }) => {
    const [loading, setLoading] = useState(false);
    const [assignedAgents, setAssignedAgents] = useState([]);
    const [availableAgents, setAvailableAgents] = useState([]);
    const [selectedAgentId, setSelectedAgentId] = useState(null);

    const fetchData = async () => {
        if (!campaign?.id || !visible) return;

        setLoading(true);
        try {
            const [assignedRes, availableRes] = await Promise.all([
                campaignAgentService.list(campaign.id),
                campaignAgentService.getAvailable(campaign.id)
            ]);

            if (assignedRes.status === 'success') {
                setAssignedAgents(assignedRes.data);
            }
            if (availableRes.status === 'success') {
                setAvailableAgents(availableRes.data);
            }
        } catch (error) {
            message.error('Error al cargar datos de agentes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [campaign?.id, visible]);

    const handleAssign = async () => {
        if (!selectedAgentId) return;

        setLoading(true);
        try {
            const res = await campaignAgentService.assign({
                campaign_id: campaign.id,
                user_id: selectedAgentId
            });

            if (res.status === 'success') {
                message.success('Agente asignado correctamente');
                setSelectedAgentId(null);
                fetchData();
            } else {
                message.error(res.data?.message || 'Error al asignar agente');
            }
        } catch (error) {
            message.error('Excepción al asignar agente');
        } finally {
            setLoading(false);
        }
    };

    const handleRemove = async (userId) => {
        setLoading(true);
        try {
            const res = await campaignAgentService.remove({
                campaign_id: campaign.id,
                user_id: userId
            });

            if (res.status === 'success') {
                message.success('Agente retirado de la campaña');
                fetchData();
            } else {
                message.error(res.data?.message || 'Error al retirar agente');
            }
        } catch (error) {
            message.error('Excepción al retirar agente');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Drawer
            title={
                <Space>
                    <TeamOutlined />
                    <span>Agentes: {campaign?.name}</span>
                </Space>
            }
            width={520}
            onClose={onClose}
            open={visible}
            loading={loading}
        >
            <Title level={5}>Asignar Nuevo Agente</Title>
            <Space.Compact style={{ width: '100%' }}>
                <Select
                    showSearch
                    placeholder="Buscar agente por nombre..."
                    style={{ flex: 1 }}
                    optionFilterProp="children"
                    onChange={setSelectedAgentId}
                    value={selectedAgentId}
                >
                    {availableAgents.map(agent => (
                        <Option key={agent.id} value={agent.id}>
                            {agent.name} ({agent.username})
                        </Option>
                    ))}
                </Select>
                <Button
                    type="primary"
                    icon={<UserAddOutlined />}
                    onClick={handleAssign}
                    disabled={!selectedAgentId}
                >
                    Asignar
                </Button>
            </Space.Compact>

            <Divider />

            <Title level={5}>Agentes Asignados ({assignedAgents.length})</Title>
            <List
                itemLayout="horizontal"
                dataSource={assignedAgents}
                locale={{ emptyText: <Empty description="No hay agentes asignados" /> }}
                renderItem={(item) => (
                    <List.Item
                        actions={[
                            <Popconfirm
                                title="¿Retirar agente de la campaña?"
                                onConfirm={() => handleRemove(item.id)}
                                okText="Sí, retirar"
                                cancelText="No"
                                okButtonProps={{ danger: true }}
                            >
                                <Button type="text" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        ]}
                    >
                        <List.Item.Meta
                            avatar={<Avatar icon={<UserOutlined />} />}
                            title={<Text strong>{item.name}</Text>}
                            description={
                                <Space direction="vertical" size={0}>
                                    <Text type="secondary" size="small">{item.email}</Text>
                                    <Tag color="blue" style={{ marginTop: 4 }}>{item.role_name}</Tag>
                                </Space>
                            }
                        />
                    </List.Item>
                )}
            />
        </Drawer>
    );
};

export default CampaignAgentsDrawer;
